// PaymentReconciler — Jun 2026 (Randy). LANDING-INDEPENDENT payment confirmation.
//
// Mounted globally inside the router. Every return from Stripe is a full page
// load, so this runs then (and on any app load), reconciling the logged-in
// user's paid-but-unapplied Stripe payments — creator upgrade, voucher top-up,
// affiliate gate — REGARDLESS of which page Stripe sent them back to. This is
// what makes confirmation depend on the payment completing, not on landing
// somewhere specific. It also fixes the cascade where a back-to-back upgrade +
// top-up left the user un-upgraded and therefore mis-routed to the Fan Portal:
// once the subscription applies here, we refresh the cached user and bounce
// them to the Dashboard.
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, getToken, getUser, setUser } from "@/lib/api";

export default function PaymentReconciler() {
  const nav = useNavigate();
  useEffect(() => {
    if (!getToken() || !getUser()) return;
    const search = window.location.search || "";
    const fromStripe = /(?:stripe=|voucher_topup=success|affiliate_session=)/.test(search);
    // Throttle to avoid hammering Stripe on quick reloads / StrictMode double-mount,
    // but always run immediately when we just came back from a checkout.
    try {
      const last = Number(sessionStorage.getItem("mysilo_reconcile_at") || 0);
      if (!fromStripe && Date.now() - last < 45000) return;
      sessionStorage.setItem("mysilo_reconcile_at", String(Date.now()));
    } catch { /* ignore */ }

    (async () => {
      try {
        const r = await api.post("/stripe/reconcile-uncredited", {});
        const d = r.data || {};
        if (d.subscription_activated) {
          // Refresh the cached user so the route gates immediately see "creator".
          try {
            const me = await api.get("/auth/me");
            if (me?.data) setUser(me.data);
          } catch { /* ignore */ }
          toast.success("You're now a Creator — your upgrade is active.");
          if (window.location.pathname.startsWith("/fan-portal")) {
            setTimeout(() => nav("/dashboard", { replace: true }), 900);
          }
        }
        if ((d.voucher_cents || 0) > 0) {
          toast.success(
            `Recovered $${(d.voucher_cents / 100).toFixed(2)} from your payment — held safely. It lands in your first silo the moment you create one.`
          );
        }
        if (d.affiliate_activated) {
          toast.success("🎯 Your Creator Pro upgrade is active.");
        }
      } catch (e) { /* non-fatal — the per-page confirm handlers are a backstop */
        if (typeof console !== "undefined") console.warn("[PaymentReconciler] reconcile failed (non-fatal):", e?.response?.status || e?.message || e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}
